import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiChartAttribute,
} from "@incendium/api";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { CHART_KEY_DASHBOARD_PAID_MEDIA_TABLE } from "features/analytics/constants/chartKeys";
import { useMemo, useState } from "react";
import { useIsMobile } from "Hooks/useIsMobile";
import {
  dashboardChartKey,
  revenueOrConversions,
  useIsEcommerceProject,
  withoutAddToCartMetrics,
} from "features/dashboard/utils/ecommerceMetrics";

interface IPaidMediaTabProps {
  attributes: ApiChartAttribute[];
  paidChannelAttributes: ApiChartAttribute[];
}

function PaidMediaTab({
  attributes,
  paidChannelAttributes,
}: IPaidMediaTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const [dimension, setDimension] = useState<ApiDimension>(
    ApiDimension.DIMENSION_SOURCE,
  );
  const fullAttributes = useMemo(
    () => [...attributes, ...paidChannelAttributes],
    [attributes, paidChannelAttributes],
  );
  const isMobile = useIsMobile();

  const fields = useMemo(
    () =>
      withoutAddToCartMetrics(
        [
          ApiMetric.METRIC_SPEND,
          ApiMetric.METRIC_PUBLISHER_CPC,
          ApiMetric.METRIC_PUBLISHER_CLICKS,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
          revenueOrConversions(isEcommerce),
          ...(isEcommerce ? [ApiMetric.METRIC_ROAS] : []),
        ],
        isEcommerce,
      ),
    [isEcommerce],
  );

  return (
    <>
      <ToggleButtonGroup
        color="secondary"
        value={dimension}
        exclusive
        onChange={(event, value) => value && setDimension(value)}
      >
        <ToggleButton value={ApiDimension.DIMENSION_SOURCE}>
          Source
        </ToggleButton>
        <ToggleButton value={ApiDimension.DIMENSION_CAMPAIGN}>
          Campaign Name
        </ToggleButton>
        <ToggleButton value={ApiDimension.DIMENSION_CONTENT}>
          Content
        </ToggleButton>
        <ToggleButton value={ApiDimension.DIMENSION_TERM}>Term</ToggleButton>
      </ToggleButtonGroup>
      <Box
        sx={{
          minHeight: 0,
          flexShrink: 0,
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_PAID_MEDIA_TABLE,
            isEcommerce,
          )}
          staticAttributes={fullAttributes}
          ignoreAttrModel
          chart={{
            name: "Paid Media Performance",
            dimension: [dimension],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            comparison: true,
            attributes: [],
            displayOptions: {
              rowsPerPage: 0,
              showTotals: true,
              tableColWidths: {
                [ApiDimension.DIMENSION_SOURCE]: isMobile ? 200 : 300,
                [ApiDimension.DIMENSION_CAMPAIGN]: isMobile ? 200 : 350,
                [ApiDimension.DIMENSION_CONTENT]: isMobile ? 200 : 350,
                [ApiDimension.DIMENSION_TERM]: isMobile ? 200 : 300,
              },
            },
            yAxisKeys: [
              {
                key: "l",
                fields,
              },
            ],
          }}
        />
      </Box>
    </>
  );
}

export default withPaidChannelAttributes(PaidMediaTab);
